import api from './axios';

export type TargetScope = 'tenant' | 'registry';

export interface FolderMigration {
  scriptId: number;
  folder: string;
  filename: string;
  folderPath: string;
  scope: 'tenant' | 'registry';
  scopeInferred: boolean;
  contentHash: string;
  version: number;
  contentChangedSinceLast: boolean;
  registeredAt: string;
  appliedTo: Record<string, {
    version: number;
    lastRun: string;
    status: string;
    /** Only on failures — see sqlErrorClassifier.js on the server. */
    errorKind?: MigrationErrorKind;
    errorReason?: string;
    errorDetail?: string;
    errorCode?: number | null;
  }>;
  archived: boolean;
  archivedAt: string | null;
  archivedReason: string | null;
}

export interface MigrationScript {
  id: number;
  name: string;
  version: number;
  description: string;
  target_scope: TargetScope;
  created_by: string;
  created_at: string;
}

export interface MigrationScriptDetail extends MigrationScript {
  sql_content: string;
}

export interface ParsedStatement {
  sql: string;
  kind: 'DDL' | 'DML' | 'DESTRUCTIVE' | 'READ';
}

export interface DryRunPreview {
  rowCounts: Array<number | null>;
  schemaChecks: Array<{ tableExists: boolean; name: string } | null>;
  error?: string;
}

export interface DryRunResult {
  statements: ParsedStatement[];
  previews: Record<string, DryRunPreview>;
}

export interface MigrationRun {
  id: number;
  run_id: string;
  migration_id: number | null;
  migration_version: number | null;
  target_db: string;
  company_id: string | null;
  status: 'pending' | 'running' | 'success' | 'failed' | 'rolled_back';
  error_message: string | null;
  executed_by: string;
  executed_at: string;
  duration_ms: number | null;
  backup_id: string | null;
  /** Present only on failures. The server classifies the driver error so the
   *  list can show a sentence instead of a dump, and so an "already applied"
   *  error is not presented as a real failure. */
  error_code?: number | null;
  error_kind?: MigrationErrorKind;
  error_reason?: string;
  error_detail?: string;
  /** false for `already_applied` — there is nothing for the operator to fix. */
  error_actionable?: boolean;
}

export type MigrationErrorKind =
  | 'already_applied'
  | 'data_conflict'
  | 'missing_object'
  | 'syntax'
  | 'permission'
  | 'unknown';

export type StatementKind = 'DDL' | 'DML' | 'DESTRUCTIVE' | 'READ';

export interface RunStatement {
  sql: string;
  kind: StatementKind;
  summary: string;
}

export interface RunTarget {
  id: number;
  runId: string;
  targetDb: string;
  companyId: string | null;
  companyName: string | null;
  companyCode: string | null;
  status: MigrationRun['status'];
  errorMessage: string | null;
  executedBy: string;
  executedAt: string;
  durationMs: number | null;
  backupId: string | null;
}

export interface RunDetail {
  runId: string;
  sqlSnapshot: string;
  scriptId: number | null;
  scriptVersion: number | null;
  statements: RunStatement[];
  summary: {
    counts: { DDL: number; DML: number; DESTRUCTIVE: number; READ: number };
    hasDestructive: boolean;
  };
  targets: RunTarget[];
}

export const databaseMigrationsApi = {
  listScripts: (scope?: TargetScope) =>
    api.get<{ scripts: MigrationScript[] }>('/super-admin/database/migrations/scripts', { params: { scope } })
      .then((r) => r.data.scripts),

  getScript: (id: number) =>
    api.get<{ script: MigrationScriptDetail; versions: MigrationScript[] }>(`/super-admin/database/migrations/scripts/${id}`)
      .then((r) => r.data),

  createScript: (body: { name: string; description?: string; sql_content: string; target_scope: TargetScope }) =>
    api.post<{ id: number; name: string; version: number }>('/super-admin/database/migrations/scripts', body)
      .then((r) => r.data),

  updateScript: (id: number, body: { description?: string; sql_content: string }) =>
    api.put<{ id: number; version: number }>(`/super-admin/database/migrations/scripts/${id}`, body)
      .then((r) => r.data),

  deleteScript: (id: number) =>
    api.delete(`/super-admin/database/migrations/scripts/${id}`).then(() => undefined),

  dryRun: (body: { sql: string; targets: string[] }) =>
    api.post<DryRunResult>('/super-admin/database/migrations/dry-run', body).then((r) => r.data),

  run: (body: { sql: string; targets: string[]; backup: boolean; scriptId?: number | null }) =>
    api.post<{ runId: string }>('/super-admin/database/migrations/run', body).then((r) => r.data.runId),

  rollback: (runId: string, tenantsToRollback: string[]) =>
    api.post<{ rolledBack: string[] }>(`/super-admin/database/migrations/run/${runId}/rollback`, { tenantsToRollback })
      .then((r) => r.data),

  listRuns: (filters: { scriptId?: number; companyId?: string; status?: string; limit?: number } = {}) =>
    api.get<{ runs: MigrationRun[] }>('/super-admin/database/migrations/runs', { params: filters })
      .then((r) => r.data.runs),

  getRun: (runId: string) =>
    api.get<RunDetail>(`/super-admin/database/migrations/runs/${runId}`).then((r) => r.data),

  appliedTenants: (scriptId: number) =>
    api.get<{ applied: Array<{ company_id: string; target_db: string; last_run: string; last_status: string }> }>(
      `/super-admin/database/migrations/scripts/${scriptId}/applied`
    ).then((r) => r.data.applied),

  listFolderMigrations: () =>
    api.get<{ migrations: FolderMigration[] }>('/super-admin/database/migrations/folders')
      .then((r) => r.data.migrations),

  listPending: (targets: string[]) =>
    api.get<{ migrations: FolderMigration[] }>('/super-admin/database/migrations/pending', { params: { targets: targets.join(',') } })
      .then((r) => r.data.migrations),

  markApplied: (migrationIds: number[], targets: string[], reason?: string): Promise<{ runId: string; recorded: number }> =>
    api.post('/super-admin/database/migrations/mark-applied', { migrationIds, targets, reason })
      .then((r) => r.data),

  archiveMigrations: (folderPaths: string[], reason?: string): Promise<{ archived: number }> =>
    api.post('/super-admin/database/migrations/archive', { folderPaths, reason }).then((r) => r.data),

  unarchiveMigrations: (folderPaths: string[]): Promise<{ unarchived: number }> =>
    api.post('/super-admin/database/migrations/unarchive', { folderPaths }).then((r) => r.data),
};
